import updateTabNames from '@/features/factsheet/lib/updateTabNames';
import { addStringIfNotExists } from '@/shared/lib/array/addStringIfNotExists';
import { splitCommaSeparatedKeys } from '@/shared/lib/format/splitCommaSeparatedKeys';

export interface FactsheetTabsState {
  sourceKeys: string;
  activeTab: number;
  openedFactsheets: string[];
  tabNames: Map<string, string>;
}

export type FactsheetTabsAction =
  | { type: 'reset'; factsheetKeys: string }
  | { type: 'changeTab'; index: number }
  | { type: 'closeTab'; index: number }
  | { type: 'openTab'; code: string }
  | { type: 'renameTab'; code: string; name: string };

export function removeAtIndex<T>(array: T[], index: number): T[] {
  return array.toSpliced(index, 1);
}

export function createFactsheetTabsState(factsheetKeys: string): FactsheetTabsState {
  const codes = splitCommaSeparatedKeys(factsheetKeys);
  return {
    sourceKeys: factsheetKeys,
    openedFactsheets: codes,
    activeTab: Math.max(codes.length - 1, 0),
    tabNames: new Map(codes.map(code => [code, code]))
  };
}

export function factsheetTabsReducer(
  state: FactsheetTabsState,
  action: FactsheetTabsAction
): FactsheetTabsState {
  switch (action.type) {
    case 'reset':
      return createFactsheetTabsState(action.factsheetKeys);

    case 'changeTab':
      return { ...state, activeTab: action.index };

    case 'closeTab': {
      const openedFactsheets = removeAtIndex(state.openedFactsheets, action.index);
      const activeTab =
        action.index <= state.activeTab ? Math.max(state.activeTab - 1, 0) : state.activeTab;
      // Keep sourceKeys in lockstep with the tabs we now hold: consumers may
      // echo this change back through props (e.g. via onOpenTabsChange), and
      // once that lands it must match what we already applied here, or the
      // mismatch check re-triggers a reset that clobbers activeTab/tabNames.
      return { ...state, openedFactsheets, activeTab, sourceKeys: openedFactsheets.join(',') };
    }

    case 'openTab': {
      const existingIndex = state.openedFactsheets.indexOf(action.code);
      if (existingIndex !== -1) {
        return { ...state, activeTab: existingIndex };
      }

      const openedFactsheets = addStringIfNotExists(state.openedFactsheets, action.code);
      return {
        ...state,
        openedFactsheets,
        activeTab: openedFactsheets.length - 1,
        tabNames: updateTabNames(state.tabNames, action.code, action.code),
        sourceKeys: openedFactsheets.join(',')
      };
    }

    case 'renameTab':
      return { ...state, tabNames: updateTabNames(state.tabNames, action.code, action.name) };

    default:
      return state;
  }
}
